import React, { useState, useEffect } from "react";
import { Row, Col, Button, Input, Table, Spinner, Badge, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import axios from "axios";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";
import FotoRegistro from "../../Produccion/Cortes/FotoRegistro";
import { SelectTipoHabilitacion, FiltroTipoHabilitacion, coincideTipo } from "../../Materiales/Habilitacion/TiposHabilitacion";

/**
 * Explosion de materiales de UN articulo de un pedido, capturada como en el
 * Excel de la Orden de Produccion:
 *  - Tela: tela, color de prenda al que aplica y consumo por prenda.
 *  - Habilitacion para maquila y para empaque: un renglon por rubro de la
 *    plantilla (BOTÓN, ETIQ. DE PRECIO...). Se elige cual del catalogo se usa
 *    (p. ej. cual boton) o se captura marca, color y proveedor; si esa
 *    combinacion no existe, el back la da de alta al guardar.
 *  - Otras habilitaciones: las que no estan en la plantilla (GANCHO, TALLERO...).
 *
 * Dos modos:
 *  - Con `pedido`: lee y guarda directo (POST /explosionGlobal/articulo); el back
 *    calcula el total con las piezas reales de cada color.
 *  - Sin `pedido` (Nuevo Pedido, aun sin guardar): devuelve los renglones con
 *    `onListo` y la pantalla los guarda junto con el pedido (`renglonParaGuardar`).
 *
 * "Copiar de otro articulo" trae la explosion del pedido mas reciente de ese
 * articulo (o su receta, si nunca se ha explotado en un pedido). En Nuevo
 * Pedido, `otrasExplosiones` ofrece tambien las de los otros articulos que se
 * estan capturando en la misma pantalla.
 *
 * `piezasPorColor` ({ idColor: piezas }) solo sirve para mostrar el total
 * estimado mientras se captura; si no se pasa y hay pedido, se calcula con los
 * renglones del pedido (y de ahi salen tambien los colores de prenda).
 */
const URL_EXPLOSION_GLOBAL = process.env.REACT_APP_URL_EXPLOSION_GLOBAL;
const URL_EXPLOSION = process.env.REACT_APP_URL_EXPLOSION;
const URL_TELAS = process.env.REACT_APP_URL_TELAS;
const URL_HABILITACION = process.env.REACT_APP_URL_HABILITACION;
const URL_ARTICULOS_PEDIDO = process.env.REACT_APP_URL_ARTICULOS_PEDIDO;
const URL_ARTICULOS = process.env.REACT_APP_URL_ARTICULOS;

const encabezados = () => ({
  headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
});
const idDe = (v) => (Array.isArray(v) ? idDe(v[0]) : v && v._id ? v._id : v || "");

// Los catalogos se piden una vez por sesion de pantalla.
let cacheTelas = null;
let cacheHabilitacion = null;
let cacheArticulos = null;
let cachePlantilla = null;

// Mismo emparejado que el back (services/PlantillaHabilitacion): el nombre del
// rubro sin acentos, el primer rubro que surte ese tipo del catalogo, o que el
// tipo empiece con el rubro ("HERRAJES (PLACAMETALICA)" es HERRAJES).
const norm = (t) =>
  String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().replace(/\s+/g, " ").trim();
const raiz = (t) => norm(t).replace(/\s*\(.*\)\s*$/, "").replace(/ES$|S$/, "");
function rubroDe(plantilla, tipo) {
  const t = norm(tipo);
  if (!t) return null;
  const exacto = plantilla.find((r) => norm(r.rubro) === t);
  if (exacto) return exacto.rubro;
  const surte = plantilla.find((r) => (r.tipos || []).some((x) => norm(x) === t));
  if (surte) return surte.rubro;
  const prefijos = plantilla.filter((r) => t.startsWith(norm(r.rubro)) || raiz(t) === raiz(r.rubro));
  const mejor = prefijos.sort((a, b) => norm(b.rubro).length - norm(a.rubro).length)[0];
  return mejor ? mejor.rubro : null;
}
const tipoDe = (h) => (h ? h.tipo || h.nombre || "" : "");

const CAMPOS_HAB = ["marca", "color", "proveedor"];
const OTRA = "__otra__";

// Alta rapida de habilitacion (para "Otras habilitaciones").
const HAB_NUEVA = { nombre: "", tipo: "", marca: "", color: "", proveedor: "", unidad: "Piezas", costo: 0 };

const renglonNuevo = (tipo = "Tela", extra = {}) => ({
  key: uuidv4(),
  _id: null,
  tipo,
  telas: "",
  habilitacion: "",
  colores: "",
  cantidadPrenda: "",
  observaciones: "NA",
  rubro: "",
  nueva: null,
  ...extra,
});

/** Renglon sin nada capturado: no se guarda (y si ya existia, se borra). */
const vacio = (r) =>
  !(Number(r.cantidadPrenda) > 0) &&
  !(r.tipo === "Tela" ? r.telas : r.habilitacion) &&
  !(r.nueva && CAMPOS_HAB.some((k) => String(r.nueva[k] || "").trim()));

/** Lo que se manda a POST /explosionGlobal/articulo (tambien lo usa Nuevo Pedido). */
export function renglonParaGuardar(r) {
  const esHab = r.tipo === "Habilitacion";
  return {
    _id: r._id || undefined,
    tipo: r.tipo,
    telas: r.tipo === "Tela" ? r.telas : undefined,
    habilitacion: esHab && r.habilitacion ? r.habilitacion : undefined,
    nueva:
      esHab && !r.habilitacion && r.nueva
        ? { tipo: r.rubro, ...Object.fromEntries(CAMPOS_HAB.map((k) => [k, String(r.nueva[k] || "").trim()])) }
        : undefined,
    rubro: esHab ? r.rubro || "" : "",
    colores: r.tipo === "Tela" && r.colores ? r.colores : undefined,
    cantidadPrenda: Number(r.cantidadPrenda),
    observaciones: r.observaciones || "NA",
  };
}

function ExplosionArticulo({
  isOpen,
  toggle,
  pedido,
  articulo,
  piezasPorColor: piezasProp,
  renglonesIniciales,
  onListo,
  otrasExplosiones = [],
}) {
  const [renglones, setRenglones] = useState([]);
  const [borrar, setBorrar] = useState([]);
  const [telas, setTelas] = useState(cacheTelas || []);
  const [habilitacion, setHabilitacion] = useState(cacheHabilitacion || []);
  const [plantilla, setPlantilla] = useState(cachePlantilla || []);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [lineas, setLineas] = useState([]);
  const [copiando, setCopiando] = useState(false);
  const [articulosCatalogo, setArticulosCatalogo] = useState(cacheArticulos || []);
  const [origenCopia, setOrigenCopia] = useState(null);
  // { key del renglon, id (si se edita una que ya existe), datos } mientras se da de alta o se edita.
  const [altaHab, setAltaHab] = useState(null);
  const [guardandoHab, setGuardandoHab] = useState(false);
  const [filtroTipoOtras, setFiltroTipoOtras] = useState("");
  // Composicion de tela: texto del articulo (no es un material).
  const [composicion, setComposicion] = useState("");
  const [composicionOriginal, setComposicionOriginal] = useState("");

  const habPorId = (id) => habilitacion.find((h) => h._id === id);
  // Rubro de un renglon de habilitacion: el elegido al capturar o el del tipo de su habilitacion.
  const rubroEf = (r) => r.rubro || rubroDe(plantilla, tipoDe(habPorId(r.habilitacion))) || "";
  const rubroTexto = (plantilla.find((r) => r.texto) || {}).rubro || "";
  // Renglones de un rubro de texto capturados antes como habilitacion: ya no cuentan y se borran.
  const esDeTexto = (r) => r.tipo === "Habilitacion" && rubroTexto && rubroEf(r) === rubroTexto;

  // Piezas y colores: los que manda la pantalla o, si no, los del pedido.
  const piezasPorColor = piezasProp || {};
  const coloresDelPedido = [];
  if (!piezasProp) {
    lineas.forEach((l) => {
      piezasPorColor[l.idColor] = (piezasPorColor[l.idColor] || 0) + l.cantidad;
      if (!coloresDelPedido.some((c) => c._id === l.idColor)) coloresDelPedido.push({ _id: l.idColor, name: l.color });
    });
  }
  const coloresArticulo = coloresDelPedido.length ? coloresDelPedido : (articulo && articulo.colores) || [];
  const totalPiezas = Object.values(piezasPorColor).reduce((t, n) => t + (Number(n) || 0), 0);

  useEffect(() => {
    if (!isOpen || !articulo) return;
    let vigente = true;
    setBorrar([]);
    setAltaHab(null);
    const cargas = [];
    if (!cacheTelas) {
      cargas.push(
        axios.get(URL_TELAS, encabezados()).then((r) => {
          cacheTelas = (r.data || []).filter((t) => t.is_active !== "No");
          if (vigente) setTelas(cacheTelas);
        })
      );
    }
    if (!cacheHabilitacion) {
      cargas.push(
        axios.get(URL_HABILITACION, encabezados()).then((r) => {
          cacheHabilitacion = (r.data || []).filter((h) => h.is_active !== "No");
          if (vigente) setHabilitacion(cacheHabilitacion);
        })
      );
    }
    if (!cachePlantilla) {
      cargas.push(
        axios.get(`${URL_HABILITACION}/plantilla`, encabezados()).then((r) => {
          cachePlantilla = r.data || [];
          if (vigente) setPlantilla(cachePlantilla);
        })
      );
    }
    setComposicion("");
    setComposicionOriginal("");
    cargas.push(
      axios.get(`${URL_ARTICULOS}/${articulo._id}`, encabezados()).then((r) => {
        if (!vigente) return;
        const texto = (r.data && r.data.composicion) || "";
        setComposicion(texto);
        setComposicionOriginal(texto);
      })
    );
    if (pedido && !piezasProp) {
      cargas.push(
        axios.get(`${URL_ARTICULOS_PEDIDO}/pedidos/${pedido}`, encabezados()).then((r) => {
          if (!vigente) return;
          setLineas(
            (r.data || [])
              .filter((a) => idDe(a.articulos) === articulo._id && a.is_active !== "No")
              .map((a) => ({ idColor: idDe(a.colores), color: a.colores?.[0]?.name, cantidad: a.cantidad || 0 }))
          );
        })
      );
    }
    if (pedido) {
      setCargando(true);
      cargas.push(
        axios
          .get(`${URL_EXPLOSION_GLOBAL}Pedido/${pedido}?articulo=${articulo._id}`, encabezados())
          .then((r) => {
            if (!vigente) return;
            setRenglones(
              (r.data || [])
                .filter((e) => e.is_active !== "No")
                .map((e) =>
                  renglonNuevo(e.telas && e.telas.length ? "Tela" : "Habilitacion", {
                    key: e._id,
                    _id: e._id,
                    telas: idDe(e.telas),
                    habilitacion: idDe(e.habilitacion),
                    colores: idDe(e.colores),
                    cantidadPrenda: e.cantidadPrenda || "",
                    observaciones: e.observaciones || "NA",
                    rubro: e.rubro || "",
                  })
                )
            );
          })
      );
    } else {
      setRenglones(renglonesIniciales && renglonesIniciales.length ? renglonesIniciales : []);
    }
    Promise.all(cargas)
      .catch(() => Swal.fire("Error", "No se pudo cargar la explosión", "error"))
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pedido, articulo && articulo._id]);

  const cambiar = (key, campo, valor) =>
    setRenglones((previos) => previos.map((r) => (r.key === key ? { ...r, [campo]: valor } : r)));

  const quitar = (r) => {
    if (r._id) setBorrar((b) => [...b, r._id]);
    setRenglones((previos) => previos.filter((x) => x.key !== r.key));
  };

  // ---- Renglones de la plantilla (un rubro). El renglon "virtual" de un rubro sin
  // captura nace al primer cambio.
  const cambiarRubro = (fila, cambios) => {
    setRenglones((previos) =>
      previos.some((r) => r.key === fila.key)
        ? previos.map((r) => (r.key === fila.key ? { ...r, ...cambios } : r))
        : [...previos, { ...fila, ...cambios }]
    );
  };

  const elegirDeRubro = (fila, valor) => {
    const cantidad = fila.cantidadPrenda || 1; // En el Excel casi todo es 1 por prenda.
    if (!valor) return cambiarRubro(fila, { habilitacion: "", nueva: null });
    if (valor === OTRA) return cambiarRubro(fila, { habilitacion: "", nueva: { marca: "", color: "", proveedor: "" }, cantidadPrenda: cantidad });
    return cambiarRubro(fila, { habilitacion: valor, nueva: null, cantidadPrenda: cantidad });
  };

  // Al escribir marca/color/proveedor: si la combinacion ya existe se elige esa;
  // si no, queda como nueva y el back la da de alta al guardar.
  const capturarDato = (fila, campo, valor, opciones) => {
    const actual = fila.nueva || Object.fromEntries(CAMPOS_HAB.map((k) => [k, (habPorId(fila.habilitacion) || {})[k] || ""]));
    const datos = { ...actual, [campo]: valor };
    const igual = opciones.find((h) => CAMPOS_HAB.every((k) => norm(h[k]) === norm(datos[k])));
    cambiarRubro(fila, {
      habilitacion: igual ? igual._id : "",
      nueva: igual ? null : datos,
      cantidadPrenda: fila.cantidadPrenda || 1,
    });
  };

  /** Copia la receta del articulo (Explosion) como punto de partida. */
  const traerReceta = async () => {
    try {
      const r = await axios.get(`${URL_EXPLOSION}Articulo/${articulo._id}`, encabezados());
      const receta = (r.data || []).filter((e) => e.is_active !== "No");
      if (!receta.length) {
        Swal.fire("Sin receta", "Este artículo no tiene explosión registrada en Artículos", "info");
        return;
      }
      const nuevos = receta.map((e) =>
        renglonNuevo(e.telas && e.telas.length ? "Tela" : "Habilitacion", {
          telas: idDe(e.telas),
          habilitacion: idDe(e.habilitacion),
          cantidadPrenda: e.cantidad || "",
          observaciones: e.observaciones || "NA",
          rubro: e.rubro || "",
        })
      );
      setRenglones((previos) => [...previos.filter((x) => !vacio(x) || x._id), ...nuevos]);
    } catch (e) {
      Swal.fire("Error", "No se pudo leer la receta del artículo", "error");
    }
  };

  /** Agrega renglones copiados; el color se conserva solo si este articulo lo tiene. */
  const agregarCopiados = (copiados) => {
    const coloresValidos = new Set(coloresArticulo.map((c) => c._id));
    let sinColor = 0;
    const nuevos = copiados.map((e) => {
      const color = e.colores && coloresValidos.has(e.colores) ? e.colores : "";
      if (e.colores && !color) sinColor += 1;
      return renglonNuevo(e.tipo, { ...e, key: uuidv4(), _id: null, colores: color });
    });
    setRenglones((previos) => [...previos.filter((x) => !vacio(x) || x._id), ...nuevos]);
    if (sinColor) {
      Swal.fire(
        "Revisa los colores",
        `${sinColor} renglón(es) eran de un color que este artículo no tiene; quedaron para todas las piezas.`,
        "info"
      );
    }
  };

  const abrirCopia = async () => {
    setCopiando(true);
    if (!cacheArticulos) {
      try {
        const r = await axios.get(URL_ARTICULOS, encabezados());
        cacheArticulos = (r.data || []).filter((a) => a.is_active !== "No");
        setArticulosCatalogo(cacheArticulos);
      } catch (e) {
        Swal.fire("Error", "No se pudieron cargar los artículos", "error");
      }
    }
  };

  const copiarDe = async (opcion) => {
    if (!opcion) return;
    // Otro articulo capturado en esta misma pantalla (aun sin guardar).
    if (opcion.pendiente) {
      agregarCopiados(opcion.renglones);
      setCopiando(false);
      setOrigenCopia(null);
      return;
    }
    try {
      const r = await axios.get(`${URL_EXPLOSION_GLOBAL}Articulo/${opcion._id}`, encabezados());
      const filas = (r.data || []).filter((e) => e.is_active !== "No" && Number(e.cantidadPrenda) > 0);
      let copiados = [];
      if (filas.length) {
        // La del pedido mas reciente de ese articulo.
        const fecha = (e) => String(e.createdAt || "");
        const reciente = filas.reduce((a, b) => (fecha(b) > fecha(a) ? b : a));
        const idPedido = idDe(reciente.pedidos);
        copiados = filas
          .filter((e) => idDe(e.pedidos) === idPedido)
          .map((e) => ({
            tipo: e.telas && e.telas.length ? "Tela" : "Habilitacion",
            telas: idDe(e.telas),
            habilitacion: idDe(e.habilitacion),
            colores: idDe(e.colores),
            cantidadPrenda: e.cantidadPrenda,
            observaciones: e.observaciones || "NA",
            rubro: e.rubro || "",
          }));
      } else {
        const receta = await axios.get(`${URL_EXPLOSION}Articulo/${opcion._id}`, encabezados());
        copiados = (receta.data || [])
          .filter((e) => e.is_active !== "No")
          .map((e) => ({
            tipo: e.telas && e.telas.length ? "Tela" : "Habilitacion",
            telas: idDe(e.telas),
            habilitacion: idDe(e.habilitacion),
            colores: "",
            cantidadPrenda: e.cantidad || "",
            observaciones: e.observaciones || "NA",
            rubro: e.rubro || "",
          }));
      }
      if (!copiados.length) {
        Swal.fire("Sin explosión", `${opcion.codigo} no tiene explosión ni receta registrada`, "info");
        return;
      }
      agregarCopiados(copiados);
      setCopiando(false);
      setOrigenCopia(null);
    } catch (e) {
      Swal.fire("Error", "No se pudo copiar la explosión", "error");
    }
  };

  const opcionesCopia = [
    ...otrasExplosiones
      .filter((o) => o.articulo && o.articulo._id !== (articulo && articulo._id) && o.renglones && o.renglones.length)
      .map((o) => ({
        _id: `pendiente-${o.articulo._id}`,
        etiqueta: `${o.articulo.codigo} (de este pedido)`,
        pendiente: true,
        renglones: o.renglones,
      })),
    ...articulosCatalogo
      .filter((a) => a._id !== (articulo && articulo._id))
      .map((a) => ({ _id: a._id, codigo: a.codigo, etiqueta: `${a.codigo} ${a.codigoCliente || ""} ${a.descripcion || ""}` })),
  ];

  const piezasDe = (r) => (r.colores ? Number(piezasPorColor[r.colores]) || 0 : totalPiezas);
  const formato = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

  // Lo capturado, con el rubro ya resuelto; lo vacio no cuenta.
  const capturados = () => renglones.filter((r) => !vacio(r) && !esDeTexto(r)).map((r) => (r.tipo === "Habilitacion" ? { ...r, rubro: rubroEf(r) } : r));

  const faltan = () => {
    const errores = [];
    capturados().forEach((r) => {
      const nombre = r.tipo === "Tela" ? "tela" : r.rubro || "habilitación";
      if (r.tipo === "Tela" && !r.telas) errores.push("falta elegir la tela");
      if (r.tipo === "Habilitacion" && !r.habilitacion && !r.nueva) errores.push(`${nombre}: elige cuál`);
      if (r.tipo === "Habilitacion" && !r.habilitacion && r.nueva && !r.rubro) errores.push("otra habilitación sin elegir");
      if (!(Number(r.cantidadPrenda) > 0)) errores.push(`${nombre}: piezas por prenda`);
    });
    return [...new Set(errores)];
  };

  const guardar = async () => {
    const errores = faltan();
    if (errores.length) {
      Swal.fire("Faltan datos", errores.join(", "), "warning");
      return;
    }
    const listos = capturados();
    // Lo que ya existia y se dejo vacio se borra.
    const borrados = [...borrar, ...renglones.filter((r) => r._id && (vacio(r) || esDeTexto(r))).map((r) => r._id)];
    // La composicion se guarda en el articulo (sale en la Orden de Produccion).
    if (composicion.trim() !== composicionOriginal.trim()) {
      try {
        await axios.patch(`${URL_ARTICULOS}/${articulo._id}`, { composicion: composicion.trim() }, encabezados());
        setComposicionOriginal(composicion.trim());
      } catch (error) {
        Swal.fire(
          "No se pudo guardar la composición",
          (error.response && error.response.data && error.response.data.message) || "Intenta de nuevo",
          "error"
        );
        return;
      }
    }
    if (!pedido) {
      onListo && onListo(listos);
      toggle();
      return;
    }
    setGuardando(true);
    try {
      await axios.post(
        `${URL_EXPLOSION_GLOBAL}/articulo`,
        { pedidos: pedido, articulos: articulo._id, renglones: listos.map(renglonParaGuardar), borrar: borrados },
        encabezados()
      );
      // Las habilitaciones nuevas que dio de alta el back: se recarga el catalogo.
      if (listos.some((r) => r.tipo === "Habilitacion" && !r.habilitacion)) cacheHabilitacion = null;
      Swal.fire("Listo", "Explosión guardada", "success");
      onListo && onListo(listos);
      toggle();
    } catch (error) {
      Swal.fire(
        "No se pudo guardar",
        (error.response && error.response.data && error.response.data.message) || "Intenta de nuevo",
        "error"
      );
    } finally {
      setGuardando(false);
    }
  };

  // ---- Alta / edicion rapida de una habilitacion (para "Otras habilitaciones").
  const crearHabilitacion = async () => {
    const d = altaHab.datos;
    setGuardandoHab(true);
    try {
      const nombre = d.nombre.trim() || [d.tipo, d.marca, d.color].filter((x) => x.trim()).join(" ");
      const cuerpo = {
        nombre,
        costo: Number(d.costo) || 0,
        unidad: d.unidad,
        tipo: d.tipo.trim(),
        marca: d.marca.trim(),
        color: d.color.trim(),
        proveedor: d.proveedor.trim(),
      };
      const { data } = altaHab.id
        ? await axios.patch(`${URL_HABILITACION}/${altaHab.id}`, cuerpo, encabezados())
        : await axios.post(
            URL_HABILITACION,
            { ...cuerpo, descripcion: d.tipo.trim() || nombre, observaciones: "NA" },
            encabezados()
          );
      cacheHabilitacion = altaHab.id
        ? (cacheHabilitacion || habilitacion).map((h) => (h._id === data._id ? data : h))
        : [...(cacheHabilitacion || habilitacion), data];
      setHabilitacion(cacheHabilitacion);
      cambiar(altaHab.key, "habilitacion", data._id);
      setAltaHab(null);
    } catch (error) {
      Swal.fire(
        "No se pudo guardar la habilitación",
        (error.response && error.response.data && error.response.data.message) || "Intenta de nuevo",
        "error"
      );
    } finally {
      setGuardandoHab(false);
    }
  };

  // ---- Catalogo por rubro y sugerencias para marca/color/proveedor.
  // Un tipo puede surtir varios rubros (BORDADA: ETIQ. BORDADA y BORDADA DE TALLA).
  const porRubro = {};
  habilitacion.forEach((h) => {
    const t = norm(tipoDe(h));
    const surtidos = plantilla.filter((r) => (r.tipos || []).some((x) => norm(x) === t)).map((r) => r.rubro);
    const rubros = surtidos.length ? surtidos : [rubroDe(plantilla, tipoDe(h))].filter(Boolean);
    rubros.forEach((r) => (porRubro[r] = porRubro[r] || []).push(h));
  });
  const sugerencias = (lista, campo) =>
    [...new Set(lista.map((h) => String(h[campo] || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const proveedoresTodos = sugerencias(habilitacion, "proveedor");
  const etiquetaHab = (h) => {
    // El alias (nombre con que lo conocen) va primero: con la carga de costeo hay
    // cientos de habilitaciones del mismo grupo y proveedor.
    const partes = [h.alias, ...CAMPOS_HAB.map((k) => h[k])].filter(Boolean);
    return partes.length ? partes.join(" · ") : h.nombre || h.tipo || "(sin datos)";
  };

  const filaRubro = (rubro, fila, esPrimera, cuantas) => {
    const opciones = porRubro[rubro] || [];
    const h = habPorId(fila.habilitacion);
    const dato = (k) => (fila.nueva ? fila.nueva[k] || "" : h ? h[k] || "" : "");
    const seleccion = fila.habilitacion || (fila.nueva ? OTRA : "");
    const listaId = (k) => `sug-${k}-${norm(rubro).replace(/[^A-Z0-9]/g, "")}`;
    const capturado = !vacio(fila);
    return (
      <tr key={fila.key} style={capturado ? { background: "#f6fbf4" } : undefined}>
        <td style={{ fontWeight: esPrimera ? 600 : 400, fontSize: 13 }}>{esPrimera ? rubro : ""}</td>
        <td>
          <Input type="select" bsSize="sm" value={seleccion} onChange={(e) => elegirDeRubro(fila, e.target.value)}>
            <option value="">—</option>
            {[...opciones]
              .sort((a, b) => etiquetaHab(a).localeCompare(etiquetaHab(b)))
              .map((o) => (
                <option key={o._id} value={o._id}>
                  {etiquetaHab(o)}
                </option>
              ))}
            <option value={OTRA}>+ Otra (capturar marca, color y proveedor)</option>
          </Input>
        </td>
        <td>
          <Input
            bsSize="sm"
            type="number"
            min="0"
            step="any"
            value={fila.cantidadPrenda}
            onChange={(e) => cambiarRubro(fila, { cantidadPrenda: e.target.value })}
          />
        </td>
        {CAMPOS_HAB.map((k) => (
          <td key={k}>
            <Input
              bsSize="sm"
              list={listaId(k)}
              value={dato(k)}
              onChange={(e) => capturarDato(fila, k, e.target.value, opciones)}
            />
            <datalist id={listaId(k)}>
              {(k === "proveedor" ? proveedoresTodos : sugerencias(opciones, k)).map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </td>
        ))}
        <td className="text-right" style={{ fontSize: 13 }}>
          {Number(fila.cantidadPrenda) > 0 ? formato(Number(fila.cantidadPrenda) * totalPiezas) : ""}
        </td>
        <td>
          {fila.habilitacion ? (
            <FotoRegistro id={fila.habilitacion} carpeta="habilitacion" tamano={34} compacto />
          ) : fila.nueva && CAMPOS_HAB.some((k) => String(fila.nueva[k] || "").trim()) ? (
            <Badge color="info" title="No existe en el catálogo: se da de alta al guardar">
              Nueva
            </Badge>
          ) : null}
        </td>
        <td style={{ whiteSpace: "nowrap" }}>
          {esPrimera ? (
            <Button
              size="sm"
              color="link"
              className="p-0 mr-2"
              title={`Agregar otro ${rubro.toLowerCase()}`}
              onClick={() => setRenglones((p) => [...p, renglonNuevo("Habilitacion", { rubro })])}
            >
              <i className="fas fa-plus" />
            </Button>
          ) : null}
          {capturado || cuantas > 1 ? (
            <Button size="sm" color="link" className="p-0 text-danger" title="Quitar" onClick={() => quitar(fila)}>
              <i className="fas fa-times" />
            </Button>
          ) : null}
        </td>
      </tr>
    );
  };

  const tablaGrupo = (grupo, titulo) => {
    const rubros = plantilla.filter((r) => r.grupo === grupo);
    return (
      <>
        <h6 className="mt-3 mb-1" style={{ fontWeight: 700 }}>
          {titulo}
        </h6>
        <Table size="sm" bordered className="mb-2">
          <thead>
            <tr>
              <th style={{ width: 170 }}>Descripción</th>
              <th style={{ width: 230 }}>¿Cuál se usa?</th>
              <th style={{ width: 80 }}>Piezas</th>
              <th>Marca</th>
              <th>Color</th>
              <th>Proveedor</th>
              <th style={{ width: 70 }} className="text-right">
                Total
              </th>
              <th style={{ width: 70 }}>Foto</th>
              <th style={{ width: 50 }}></th>
            </tr>
          </thead>
          <tbody>
            {rubros.map(({ rubro, texto }) => {
              if (texto) {
                return (
                  <tr key={rubro}>
                    <td style={{ fontWeight: 600, fontSize: 13 }}>{rubro}</td>
                    <td colSpan={8}>
                      <Input
                        bsSize="sm"
                        placeholder="Ej. 98% ALGODÓN 2% ELASTANO"
                        value={composicion}
                        onChange={(e) => setComposicion(e.target.value)}
                      />
                    </td>
                  </tr>
                );
              }
              const filas = renglones.filter((r) => r.tipo === "Habilitacion" && rubroEf(r) === rubro);
              const lista = filas.length ? filas : [renglonNuevo("Habilitacion", { key: `virtual-${rubro}`, rubro })];
              return lista.map((f, i) => filaRubro(rubro, f, i === 0, lista.length));
            })}
          </tbody>
        </Table>
      </>
    );
  };

  const opcionesTela = telas.map((m) => ({ _id: m._id, nombre: m.nombre || m.name || "" }));
  const opcionesHab = habilitacion.map((m) => ({ _id: m._id, nombre: m.nombre || m.name || "", tipo: m.tipo || "" }));
  const renglonesTela = renglones.filter((r) => r.tipo === "Tela");
  const otras = renglones.filter((r) => r.tipo === "Habilitacion" && !rubroEf(r));

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle} style={{ maxWidth: 1300 }}>
      <ModalHeader toggle={toggle}>
        Explosión {articulo ? `${articulo.codigo || ""} ${articulo.descripcion || ""}` : ""}
      </ModalHeader>
      <ModalBody>
        {cargando ? (
          <div className="text-center py-4">
            <Spinner />
          </div>
        ) : (
          <>
            <Row className="mb-2">
              <Col>
                <small className="text-muted">
                  Piezas por prenda ({formato(totalPiezas)} pzas en el pedido). En la tela, el color de prenda indica a
                  qué piezas aplica. En la habilitación elige cuál se usa o captura marca, color y proveedor: si no existe,
                  se da de alta al guardar.
                </small>
              </Col>
              <Col md="auto">
                <Button size="sm" color="info" className="mr-2" onClick={abrirCopia}>
                  <i className="fas fa-clone" /> Copiar de otro artículo
                </Button>
                <Button size="sm" color="info" onClick={traerReceta}>
                  <i className="fas fa-copy" /> Traer receta del artículo
                </Button>
              </Col>
            </Row>
            {copiando ? (
              <Row className="mb-2 align-items-center">
                <Col md={8}>
                  <Autocomplete
                    size="small"
                    value={origenCopia}
                    options={opcionesCopia}
                    getOptionLabel={(o) => (o && o.etiqueta) || ""}
                    getOptionSelected={(o, v) => o._id === v._id}
                    onChange={(e, valor) => setOrigenCopia(valor)}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" placeholder="¿De qué artículo copio la explosión?" />
                    )}
                  />
                </Col>
                <Col md="auto">
                  <Button size="sm" color="success" className="mr-2" disabled={!origenCopia} onClick={() => copiarDe(origenCopia)}>
                    Copiar
                  </Button>
                  <Button size="sm" color="light" onClick={() => setCopiando(false)}>
                    Cancelar
                  </Button>
                </Col>
              </Row>
            ) : null}

            <h6 className="mt-2 mb-1" style={{ fontWeight: 700 }}>
              Tela
            </h6>
            <Table size="sm" bordered className="mb-1">
              <thead>
                <tr>
                  <th>Tela</th>
                  <th style={{ width: 190 }}>Color de prenda</th>
                  <th style={{ width: 110 }}>Por prenda</th>
                  <th style={{ width: 100 }} className="text-right">
                    Total
                  </th>
                  <th>Observaciones</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {renglonesTela.map((r) => (
                  <tr key={r.key}>
                    <td>
                      <Autocomplete
                        size="small"
                        value={opcionesTela.find((o) => o._id === r.telas) || null}
                        options={opcionesTela}
                        getOptionLabel={(o) => (o && o.nombre) || ""}
                        getOptionSelected={(o, v) => o._id === v._id}
                        onChange={(e, valor) => cambiar(r.key, "telas", valor ? valor._id : "")}
                        renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Busca la tela" />}
                      />
                    </td>
                    <td>
                      <Input type="select" bsSize="sm" value={r.colores} onChange={(e) => cambiar(r.key, "colores", e.target.value)}>
                        <option value="">Todas las piezas</option>
                        {coloresArticulo.map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.name}
                          </option>
                        ))}
                      </Input>
                    </td>
                    <td>
                      <Input
                        bsSize="sm"
                        type="number"
                        min="0"
                        step="any"
                        value={r.cantidadPrenda}
                        onChange={(e) => cambiar(r.key, "cantidadPrenda", e.target.value)}
                      />
                    </td>
                    <td className="text-right">{formato((Number(r.cantidadPrenda) || 0) * piezasDe(r))}</td>
                    <td>
                      <Input bsSize="sm" value={r.observaciones} onChange={(e) => cambiar(r.key, "observaciones", e.target.value)} />
                    </td>
                    <td>
                      <Button title="Quitar renglón" size="sm" color="danger" onClick={() => quitar(r)}>
                        <i className="fas fa-minus" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {!renglonesTela.length ? (
                  <tr>
                    <td colSpan={6} className="text-muted" style={{ fontSize: 13 }}>
                      Sin tela capturada.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
            <Button size="sm" color="info" onClick={() => setRenglones((p) => [...p, renglonNuevo("Tela")])}>
              <i className="fas fa-plus" /> Tela
            </Button>

            {plantilla.length ? (
              <>
                {tablaGrupo("Maquila", "Habilitación para maquila")}
                {tablaGrupo("Empaque", "Habilitación para empaque")}
              </>
            ) : null}

            <h6 className="mt-3 mb-1" style={{ fontWeight: 700 }}>
              Otras habilitaciones <small className="text-muted">(las que no están en la plantilla: gancho, tallero…)</small>
              <FiltroTipoHabilitacion
                bsSize="sm"
                value={filtroTipoOtras}
                onChange={setFiltroTipoOtras}
                style={{ display: "inline-block", width: 220, marginLeft: 12, verticalAlign: "middle" }}
              />
            </h6>
            {otras.length ? (
              <Table size="sm" bordered className="mb-1">
                <thead>
                  <tr>
                    <th>Habilitación</th>
                    <th style={{ width: 110 }}>Por prenda</th>
                    <th style={{ width: 100 }} className="text-right">
                      Total
                    </th>
                    <th>Observaciones</th>
                    <th style={{ width: 70 }}>Foto</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {otras.map((r) => {
                    const h = habPorId(r.habilitacion);
                    return (
                      <tr key={r.key}>
                        <td>
                          <Autocomplete
                            size="small"
                            value={opcionesHab.find((o) => o._id === r.habilitacion) || null}
                            options={opcionesHab.filter((o) => coincideTipo(o, filtroTipoOtras))}
                            getOptionLabel={(o) => (o && o.nombre) || ""}
                            getOptionSelected={(o, v) => o._id === v._id}
                            onChange={(e, valor) => cambiar(r.key, "habilitacion", valor ? valor._id : "")}
                            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Busca" />}
                          />
                          <div className="d-flex justify-content-between align-items-center mt-1">
                            <small className="text-muted">{h ? [h.tipo, ...CAMPOS_HAB.map((k) => h[k])].filter(Boolean).join(" · ") : ""}</small>
                            <span style={{ whiteSpace: "nowrap" }}>
                              {h ? (
                                <Button
                                  size="sm"
                                  color="link"
                                  className="p-0 mr-2"
                                  onClick={() =>
                                    setAltaHab({
                                      key: r.key,
                                      id: h._id,
                                      datos: {
                                        nombre: h.nombre || "",
                                        tipo: h.tipo || "",
                                        marca: h.marca || "",
                                        color: h.color || "",
                                        proveedor: h.proveedor || "",
                                        unidad: h.unidad || "Piezas",
                                        costo: h.costo || 0,
                                      },
                                    })
                                  }
                                >
                                  Editar
                                </Button>
                              ) : null}
                              <Button
                                size="sm"
                                color="link"
                                className="p-0"
                                onClick={() => setAltaHab({ key: r.key, datos: { ...HAB_NUEVA } })}
                              >
                                + Nueva habilitación
                              </Button>
                            </span>
                          </div>
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            type="number"
                            min="0"
                            step="any"
                            value={r.cantidadPrenda}
                            onChange={(e) => cambiar(r.key, "cantidadPrenda", e.target.value)}
                          />
                        </td>
                        <td className="text-right">{formato((Number(r.cantidadPrenda) || 0) * totalPiezas)}</td>
                        <td>
                          <Input bsSize="sm" value={r.observaciones} onChange={(e) => cambiar(r.key, "observaciones", e.target.value)} />
                        </td>
                        <td>{r.habilitacion ? <FotoRegistro id={r.habilitacion} carpeta="habilitacion" tamano={34} compacto /> : null}</td>
                        <td>
                          <Button title="Quitar renglón" size="sm" color="danger" onClick={() => quitar(r)}>
                            <i className="fas fa-minus" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : null}
            <Button size="sm" color="info" onClick={() => setRenglones((p) => [...p, renglonNuevo("Habilitacion")])}>
              <i className="fas fa-plus" /> Otra habilitación
            </Button>

            {altaHab ? (
              <div className="border rounded p-2 mt-3" style={{ background: "#f8f9fa" }}>
                <h6>{altaHab.id ? "Editar habilitación" : "Nueva habilitación"}</h6>
                <Row>
                  {[
                    ["tipo", "Tipo"],
                    ["marca", "Marca"],
                    ["color", "Color"],
                    ["proveedor", "Proveedor"],
                    ["nombre", altaHab.id ? "Nombre" : "Nombre (opcional)"],
                  ].map(([campo, etiqueta]) => (
                    <Col md={campo === "nombre" ? 4 : 2} key={campo} className="mb-2">
                      <small>{etiqueta}</small>
                      {campo === "tipo" ? (
                        <SelectTipoHabilitacion
                          bsSize="sm"
                          value={altaHab.datos.tipo}
                          onChange={(v) => setAltaHab((a) => ({ ...a, datos: { ...a.datos, tipo: v } }))}
                        />
                      ) : (
                        <Input
                          bsSize="sm"
                          value={altaHab.datos[campo]}
                          onChange={(e) => {
                            const v = e.target.value;
                            setAltaHab((a) => ({ ...a, datos: { ...a.datos, [campo]: v } }));
                          }}
                        />
                      )}
                    </Col>
                  ))}
                  <Col md={2} className="mb-2">
                    <small>Unidad</small>
                    <Input
                      bsSize="sm"
                      type="select"
                      value={altaHab.datos.unidad}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAltaHab((a) => ({ ...a, datos: { ...a.datos, unidad: v } }));
                      }}
                    >
                      <option value="Piezas">Piezas</option>
                      <option value="Metros">Metros</option>
                      <option value="Kilos">Kilos</option>
                    </Input>
                  </Col>
                  <Col md={2} className="mb-2">
                    <small>Costo</small>
                    <Input
                      bsSize="sm"
                      type="number"
                      value={altaHab.datos.costo}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAltaHab((a) => ({ ...a, datos: { ...a.datos, costo: v } }));
                      }}
                    />
                  </Col>
                </Row>
                <Button size="sm" color="light" className="mr-2" onClick={() => setAltaHab(null)}>
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  color="success"
                  disabled={guardandoHab || !(altaHab.datos.tipo.trim() || altaHab.datos.nombre.trim())}
                  onClick={crearHabilitacion}
                >
                  {guardandoHab ? <Spinner size="sm" /> : <i className="fas fa-save" />} {altaHab.id ? "Guardar" : "Dar de alta"}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {faltan().length ? (
          <span className="text-muted mr-auto" style={{ fontSize: 13 }}>
            Falta: {faltan().join(", ")}
          </span>
        ) : (
          <span className="text-muted mr-auto" style={{ fontSize: 13 }}>
            {capturados().length} renglón(es) capturados
          </span>
        )}
        <Button color="light" onClick={toggle} disabled={guardando}>
          Cancelar
        </Button>
        <Button color="success" onClick={guardar} disabled={guardando || cargando || faltan().length > 0}>
          {guardando ? <Spinner size="sm" /> : <i className="fas fa-save" />} {pedido ? "Guardar explosión" : "Listo"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default ExplosionArticulo;
